<template>
  <div v-if="!meetingStartTimes.length" class="meeting">No online meeting</div>

  <div v-else class="meeting">
    <div v-if="onlyShowNextMeeting" class="text-dark">
      <h6>Your online meeting start</h6>

      <h5 class="meeting__next-meeting text-black font-weight-bold mt-2 mb-3">
        {{ nextMeetingDate || 'All online meetings have been ended' }}
      </h5>
    </div>

    <!-- week date -->
    <div class="meeting__week-day d-flex flex-wrap flex-sm-nowrap">
      <span
        v-for="(meeting, idx) in meetingDays"
        :key="idx"
        class="meeting__circle-day mb-2 mb-sm-0"
        :class="{
          'meeting__circle-day--active': meeting.active,
          [meeting.fullName.toLowerCase()]: meeting.fullName
        }"
        >{{ meeting.abbreviation }}
      </span>
    </div>

    <!-- repeat time -->
    <div class="meeting__repeat-time text__meeting-schedule text-dark">
      {{ meetingTime }}
      {{ sectionRepeatName }}
      {{ numberOfOnlineMeeting }}
      {{ numberOfOnlineMeeting > 1 ? 'meetings' : 'meeting' }}
    </div>

    <!-- show detail -->
    <div class="meeting__detail">
      <b-button
        variant="link"
        class="button__show-details meeting__show-details button__show-meetings px-0"
        @click="showModal = true"
      >
        Show details
      </b-button>
    </div>

    <ModalMeetingDetail
      :show.sync="showModal"
      :section-id="section.id"
      title="Meeting details"
      :meetings-list="meetingStartTimes"
    />
  </div>
</template>

<script>
import moment from 'moment';
import { datetimeMixin } from '@/mixins/datetimeMixin';
import { sectionMixin } from '@/mixins/sectionMixin';
import Section from '@/models/Section';
import Meeting from '@/models/Meeting';
import ModalMeetingDetail from './ModalMeetingDetail';

const MEETING_DURATION = 45;

export default {
  components: {
    ModalMeetingDetail
  },

  mixins: [datetimeMixin, sectionMixin],

  model: {
    prop: 'show',
    event: 'change'
  },

  props: {
    section: {
      type: Section,
      required: true
    },

    onlyShowNextMeeting: {
      type: Boolean,
      default: false
    },

    show: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      nextMeeting: '',
      showModal: false
    };
  },

  computed: {
    allMeeting() {
      return Meeting.query().where('section_id', this.section.id).get();
    },

    numberOfOnlineMeeting() {
      return this.allMeeting.length;
    },

    meetingTime() {
      return this.section.online_meeting_start_time
        ? `${this.hours12(this.section.online_meeting_start_time)}, `
        : '';
    },

    sectionRepeatName() {
      return this.section.section_repeat ? `${this.section.section_repeat.name}, ` : '';
    },

    isAllMeetingDone() {
      return true;
    },

    meetingStartTimes() {
      const { allMeeting = [] } = this;

      return allMeeting.map((meeting) => meeting.start_time);
    },

    nextMeetingDate() {
      let { nextMeeting = '' } = this;

      if (nextMeeting.length) {
        const parsedNextMeeting = this.formatedDatetimeWithTimezone(
          nextMeeting,
          'ddd, MMM D, YYYY hh:mma'
        );

        const parsedTimezone = parsedNextMeeting.tz.split('/').slice(1, 2);

        nextMeeting = `${parsedNextMeeting.text} ${parsedTimezone} time`;
      }

      return nextMeeting;
    },

    meetingDays() {
      if (!this.meetingStartTimes.length) return [];

      const { nextMeeting = '' } = this;

      const meetingDays = this.meetingStartTimes.map((day) => moment(day).format('dddd'));

      const uniqMeetingDays = [...new Set(meetingDays)];

      /* start week at Sunday */
      const weekDays = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday'
      ];

      const sortedMeetingDays = uniqMeetingDays.sort((a, b) =>
        weekDays.indexOf(a) > weekDays.indexOf(b) ? 1 : -1
      );

      const meetingDaysData = weekDays.map((meeting) => {
        let active = false;

        if (this.onlyShowNextMeeting) {
          if (nextMeeting.length) {
            let nextMeetingDay = moment(nextMeeting).format('dddd');

            active = nextMeetingDay.includes(meeting);
          }
        } else {
          active = sortedMeetingDays.includes(meeting);
        }

        return {
          fullName: meeting,
          abbreviation: meeting.slice(0, 1),
          active
        };
      });

      return meetingDaysData;
    }
  },

  watch: {
    nextMeetingDate: {
      immediate: true,
      handler(nextMeetingDate) {
        const show = this.onlyShowNextMeeting
          ? nextMeetingDate !== '' && nextMeetingDate.length > 0
          : true;

        this.$emit('change', show);
      }
    }
  },

  async mounted() {
    if (this.onlyShowNextMeeting) {
      this.computeNextMeetingDate();
    }
  },

  methods: {
    computeNextMeetingDate() {
      const meetings = this.meetingStartTimes;

      if (meetings.length) {
        const today = moment(); // local by default

        for (let index = 0; index < meetings.length; index++) {
          const meetingDateTime = moment(meetings[index]);

          const minDiff = meetingDateTime.diff(today, 'minutes');

          if (minDiff > -MEETING_DURATION) {
            this.nextMeeting = meetings[index];

            break;
          }
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.meeting {
  display: flex;
  flex-flow: column wrap;
  justify-content: flex-start;
  align-items: flex-start;
  // flex-basis: 30%;

  &__week-day {
    align-self: flex-start;
    margin-bottom: 0.25rem;
  }

  &__circle-day {
    color: var(--gray-dark);
    background-color: var(--light);
    font-size: 0.65rem;
    font-weight: 600;
    padding: 0.55rem 0.75rem;
    border-radius: 50%;

    &:not(:last-child) {
      margin-right: 0.5rem;
    }

    &--active {
      color: var(--white);
      background-color: var(--primary);
    }
  }

  &__repeat-time {
    margin: 0.5rem 0;
  }

  &__detail {
    position: relative;
  }

  &__show-details {
    width: 6rem;
    color: var(--primary);
    cursor: pointer;
    // position: absolute;
    // top: 0;
    // left: 0;
    transition: all 0.3s;

    &:hover,
    &:active {
      transform: translateY(-1px);
    }
  }
}
</style>
